import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';

import PersonAvatar from '../common/PersonAvatar';
import { PHOTO_ACCEPT, photoFileProblem } from '../../utils/personPhoto';

/**
 * Chooses a profile photo to be saved later with the rest of a form (the
 * application form, the student's identity). Shows the chosen photo — or the
 * one already on file — with Upload / Change / Remove.
 *
 * `value`: a newly chosen File, `null` when the saved photo is being
 * removed, `undefined` for "unchanged".
 */
const PersonPhotoInput: React.FC<{
  label?: string;
  name?: string;
  value: File | null | undefined;
  /** The photo already on file, if any. */
  currentFilename?: string | null;
  onChange: (file: File | null | undefined) => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}> = ({ label = 'Photo', name, value, currentFilename, onChange, required, error, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const previewUrl = useMemo(() => (value ? URL.createObjectURL(value) : undefined), [value]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const shownFilename = value === null ? undefined : currentFilename;
  const hasPhoto = !!value || !!shownFilename;

  const pick = (file?: File) => {
    if (!file) return;
    const p = photoFileProblem(file);
    setProblem(p);
    if (!p) onChange(file);
  };

  const message = problem || error;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <PersonAvatar name={name} src={previewUrl} photoFilename={shownFilename} size={72} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {label}{required && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}
        </Typography>
        <Typography variant="caption" color={message ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 0.75 }}>
          {message || 'A clear, recent face photo. JPG or PNG, up to 5 MB.'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhotoCamera fontSize="small" />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            {hasPhoto ? 'Change' : 'Upload photo'}
          </Button>
          {hasPhoto && (
            <Button
              size="small"
              color="inherit"
              onClick={() => { setProblem(null); onChange(value ? undefined : null); }}
              disabled={disabled}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {value && currentFilename ? 'Keep current' : 'Remove'}
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          hidden
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
        />
      </Box>
    </Stack>
  );
};

export default PersonPhotoInput;
